import React, { useState, useEffect } from "react";
import { SyncOutlined } from "@ant-design/icons";
import { CustomDataTable } from "@/components/UI/Tables/Datatable";
import { useInventoryData } from "../hooks/useInventoryData";
import "./InventoryTable.css";
import { Button } from "antd";
import moment from "moment";

/**
 * Inventory Control Table Component
 * Uses the reusable Datatable component with server-side pagination and search
 */
const InventoryTable = ({
  onAddEvent,
  onEditEvent,
  onViewEvent,
  refetchTrigger,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Use custom hook for inventory data with pagination and search
  const {
    inventoryData,
    paginationInfo,
    handlePageChange,
    handlePageSizeChange,
    search,
    handleSearch,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useInventoryData();

  // Refetch data when refetchTrigger changes
  useEffect(() => {
    if (refetchTrigger > 0) {
      refetch();
    }
  }, [refetchTrigger, refetch]);

  // Debug: Log the data
  // useEffect(() => {
  //   console.log("Inventory Data in Table:", inventoryData);
  //   console.log("Pagination Info:", paginationInfo);
  // }, [inventoryData, paginationInfo]);

  // Define columns for inventory data
  const eventColumns = [
    {
      title: "Product Name",
      dataIndex: "product_name",
      key: "name",
      width: 300,
      render: (text, record) => (
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center">
              <span className="text-gray-400 text-xs">🍽️</span>
            </div>
          </div>
          <div className="flex flex-col">
            <span className="font-medium text-gray-900">{text}</span>
            <span className="text-sm text-gray-500">PKG{record.id}</span>
          </div>
        </div>
      ),
    },
    {
      title: "Current Stock",
      dataIndex: "current_qty",
      key: "current_qty",
      width: 120,
      render: (qty) => (
        <span className="font-medium text-gray-900">{qty || 0}</span>
      ),
    },
    {
      title: "Minimum Stock",
      dataIndex: "minimum_qty",
      key: "minimum_qty",
      width: 120,
      render: (qty) => (
        <span className="font-medium text-gray-900">{qty || 0}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (_, record) => {
        // Ensure status is a string
        const status = (record?.status || "").toLowerCase();

        // Map status to display text and classes
        const statusMap = {
          "out of stock": {
            text: "Out Of Stock",
            class: "bg-red-100 text-red-800",
          },
          "low on stock": {
            text: "Low On Stock",
            class: "bg-yellow-100 text-yellow-800",
          },
          "in stock": {
            text: "In Stock",
            class: "bg-green-100 text-green-800",
          },
        };

        // Fallback if status is unknown
        const { text: statusText, class: statusClass } = statusMap[status] || {
          text: "Unknown",
          class: "bg-gray-100 text-gray-800",
        };

        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${statusClass}`}
          >
            {statusText}
          </span>
        );
      },
    },
    {
      title: "Last Restock",
      dataIndex: "lastrestocked",
      key: "lastrestocked",
      width: 120,
      render: (date) => (
        <span className="font-medium text-gray-900">
          {date ? moment(date).format("MMM DD, YYYY") : "-"}
        </span>
      ),
    },
  ];

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  // Row actions
  const rowActions = (record) => (
    <Button
      type="link"
      icon={<SyncOutlined />}
      onClick={() => onEditEvent && onEditEvent(record)}
      className="text-blue-500"
    >
      Update
    </Button>
  );

  // Handle loading and error states
  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-red-600">
          Error loading inventory: {error?.message || "Unknown error"}
        </div>
      </div>
    );
  }

  return (
    <CustomDataTable
      title="Inventory Control"
      data={inventoryData}
      columns={eventColumns}
      showSearch={true}
      searchPlaceholder="Search inventory..."
      searchMode="server"
      searchValue={search}
      onServerSearch={handleSearch}
      showFilter={false}
      showCSV={true}
      onFilterClick={() => console.log("Filter clicked")}
      onCSVClick={() => console.log("CSV clicked")}
      rowSelection={rowSelection}
      selectedRowKeys={selectedRowKeys}
      rowActions={rowActions}
      loading={isLoading || isFetching}
      paginationMode="server"
      currentPage={paginationInfo.current_page}
      totalItems={paginationInfo.total}
      serverPageSize={paginationInfo.per_page}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      pageSizeOptions={["10", "20", "50", "100"]}
      tableClassName="inventory-table"
    />
  );
};

export default InventoryTable;
